import dayjs from "dayjs";
import { Subscription } from "../model/Subscription";

export class SubscriptionPrice {
  monthly: number = 0;
  yearly: number = 0;
  monthlyForYear: number = 0;
  discountRate: number = 0;
}

const priceMap: { [key: string]: SubscriptionPrice; } = {
  'free': { monthly: 0, yearly: 0, monthlyForYear: 0, discountRate: 0 },
  'lite': { monthly: 980, yearly: 10000, monthlyForYear: 883, discountRate: 10 },
  'standard': { monthly: 3900, yearly: 38000, monthlyForYear: 3166, discountRate: 11 },
  'advanced': { monthly: 11000, yearly: 105000, monthlyForYear: 8750, discountRate: 14 },
};

const descriptionMap: { [key: string]: any; } = {
  'free': '\n・1日あたりの付与クレジット 30\n・広告非表示オプション なし\n・同時画像生成数 1 まで\n・生成画像の保存期間 30 日間\n・詳細画像設定機能 なし',
  'lite': '・1ヶ月あたり付与クレジット 1,800\n・広告非表示オプション あり\n・同時画像生成数 4 まで\n・生成画像の保存期間 90 日間\n・詳細画像設定機能 あり',
  'standard': '1ヶ月あたり付与クレジット 7,500 まで\n・広告非表示オプション あり\n・同時画像生成数 4 まで\n・生成画像の保存期間 無制限\n・詳細画像設定機能 あり',
  'advanced': '1ヶ月あたり付与クレジット 26,000 まで\n・広告非表示オプション あり\n・同時画像生成数 4 まで\n・生成画像の保存期間 無制限\n・詳細画像設定機能 あり',
};

const summaryMap: { [key: string]: any; } = {
  'free': 'トライアルに最適な無料プラン。1日ごとにクレジットがリフレッシュされます。',
  'lite': '1日の生成回数制限は無し。基本機能が全て利用可能。上位生成AIモデルも利用できるプラン',
  'standard': '生成画像の保存期間は無制限。 Liteプランよりもさらにお得に沢山の画像生成をしたい方向けの標準プラン。',
  'advanced': '最もお得なレートで大量の画像生成を実現。Drop AIを使い倒したい方におすすめの最上位プラン。',
};

const planNameMap: { [key: string]: any; } = {
  'free': 'Free',
  'lite': 'Lite',
  'standard': 'Standard',
  'advanced': 'Advanced',
};

export function getPlanPrice(planId: string, span: string) : SubscriptionPrice {
  return priceMap[planId] || { monthly: 0, yearly: 0 };
}

export function getPlanNameBySubscription(subscription: Subscription | undefined) : string {
  if (subscription === undefined) {
    return '---';
  }

  if(subscription.internalProductId === 'free') {
    return 'Free';
  }
  if(subscription.internalProductId.startsWith('lite')) {
    return 'Lite';
  }
  if(subscription.internalProductId.startsWith('standard')) {
    return 'Standard';
  }
  if(subscription.internalProductId.startsWith('advanced')) {
    return 'Advanced';
  }
  return 'invalid plan';
}

export function getPlanName(planId: string) : string {
  return planNameMap[planId] || '';
}

export function getPlanPriceText(subscription: Subscription | undefined) : string {
  if (subscription === undefined) {
    return '---';
  }

  if(subscription.internalProductId === 'free') {
    return '無料';
  }
  if(subscription.internalProductId.startsWith('lite')) {
    return '980円 / 月';
  }
  if(subscription.internalProductId.startsWith('standard')) {
    return '3,900円 / 月 ';
  }
  if(subscription.internalProductId.startsWith('advanced')) {
    return '11,000円 / 月 ';
  }
  return "未定";
}

export function getExpiryText(subscription: Subscription | undefined) : string {
  if (subscription === undefined || subscription.internalProductId === 'free') {
    return '---';
  }

  return dayjs(subscription?.currentPeriodEnd).format('YYYY/MM/DD HH:mm');
}

export function getRnewwalType(subscription: Subscription | undefined) : string {
  if (subscription === undefined || subscription.internalProductId === 'free') {
    return '---';
  }

  if(subscription.internalProductId.endsWith('month')) {
    return '月間契約';
  }
  if(subscription.internalProductId.endsWith('year')) {
    return '年間契約';
  }
  return '---';
}

export function getPlanDescription(planId: string) : string {
  return descriptionMap[planId] || '';
}

export function getPlanSummary(planId: string) : string {
  return summaryMap[planId] || '';
}

export function getNextPlanId(subscription: Subscription | undefined) : string {
  if(subscription === undefined || subscription.internalProductId === 'free') {
    return 'lite';
  }
  if(subscription.internalProductId.startsWith('lite')) {
    return 'standard';
  }
  if(subscription.internalProductId.startsWith('standard')) {
    return 'advanced';
  }
  return '';
}

export function getTransactionItemName(itemName: string, price: number = -1) : string {
  if(itemName.startsWith('credit_')) {
    const amount = itemName.split('_')[1];
    return `クレジット ${amount}`;
  }

  const planName = getPlanName(itemName) + ' プラン';
  if (price === 0) {
    return `${planName}(トライアル)`;
  }
  return planName + ' 月間サブスクリプション';
}
